import { DocumentEnum } from "@/constant/enum/document.enum";
import i18next from "@/utils/i18n";
import { z } from "zod";

export type RequestUpdateRequest = {
  name: string;
  favorite: boolean;
};

export enum FileQualityEnum {
  OK = "OK",
  MEDIUM = "MEDIUM",
  POOR = "POOR",
}

export enum FileTypeEnum {
  PDF = "PDF",
  TEXT = "TEXT",
}

export enum FileUploadSourceEnum {
  BATCH = "BATCH",
  REQUEST = "REQUEST",
}

export const InputFileDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export type InputFileDto = z.infer<typeof InputFileDtoSchema>;

export const FileInfoDtoSchema = InputFileDtoSchema.extend({
  quality: z.nativeEnum(FileQualityEnum),
  type: z.nativeEnum(FileTypeEnum),
  checksum: z.string(),
  creationDate: z.coerce.date(),
  lastUpdateDate: z.coerce.date(),
});

export type FileInfoDto = z.infer<typeof FileInfoDtoSchema>;

export const FileInfoWithContentDtoSchema = FileInfoDtoSchema.extend({
  content: z.string(),
});

export type FileInfoWithContentDto = z.infer<
  typeof FileInfoWithContentDtoSchema
>;

export const RequestUpdateResultsRequestSchema = z.object({
  docIds: z.array(z.string()),
});

export type RequestUpdateResultsRequest = z.infer<
  typeof RequestUpdateResultsRequestSchema
>;

export const RequestDtoSchema = z.object({
  id: z.string(),
  creationDate: z.coerce.date(),
  lastUpdateDate: z.coerce.date(),
  name: z.string(),
  favorite: z.boolean(),
  docTypes: z.array(z.nativeEnum(DocumentEnum)),
  startDate: z.coerce.date().nullable(),
  endDate: z.coerce.date().nullable(),
  inputText: z.string().nullable(),
  fileCount: z.number().nullable(),
  inputFiles: z.array(FileInfoDtoSchema),
});

export type RequestDto = z.infer<typeof RequestDtoSchema>;

export type RequestCreateRequest = {
  name: string;
};

export const EntitySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type Entity = z.infer<typeof EntitySchema>;

export const CountrySchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type Country = z.infer<typeof EntitySchema>;

const DocumentBaseSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(DocumentEnum),
  displayed: z.boolean(),
  entities: z.array(EntitySchema),
  universalKey: z.string(),
  title: z.preprocess((val) => {
    if (val === null) {
      return i18next.t("notField");
    }
    return val;
  }, z.string()),
  docAbstract: z.preprocess((val) => {
    if (val === null) {
      return i18next.t("notField");
    }
    return val;
  }, z.string()),
  date: z.coerce.date(),
  score: z.number(),
  countryIso3: z.array(z.string()).optional(), //Used to store iso3 cleaned generated in front, but shoudn't be here
  countryIso2: z.array(CountrySchema).optional(), //Used to store iso2 cleaned generated in front, but shoudn't be here
});

const ArticleTypeSchema = DocumentBaseSchema.extend({
  type: z.literal(DocumentEnum.ARTICLE),
  country: z.array(z.string()),
  sourceTitle: z.preprocess((val) => {
    if (val === null) {
      return i18next.t("notField");
    }
    return val;
  }, z.string()),
  sourceIssn: z.preprocess((val) => {
    if (val === null) {
      return i18next.t("notField");
    }
    return val;
  }, z.string()),
  scholarlyCitationsCount: z.number(),
  scholarlyReferencesCount: z.number(),
  fwci: z.preprocess((val) => {
    if (val === null) {
      return 0;
    }
    return val;
  }, z.number()),
  uniqueCountryCount: z.number().optional(), //Used to store unique country count generated in front, but shoudn't be here
});

export const FamilySchema = z.object({
  date: z.coerce.date(),
  docId: z.string(),
  auth: z.string(),
  kind: z.string(),
  universalKey: z.string(),
});

export type Family = z.infer<typeof FamilySchema>;

export type ArticleType = z.infer<typeof ArticleTypeSchema>;

const PatentTypeSchema = DocumentBaseSchema.extend({
  type: z.literal(DocumentEnum.PATENT),
  scope: z.number(),
  originality: z.number(),
  generality: z.number(),
  fwdCits: z.number(),
  bwdCits: z.number(),
  radicalness: z.number(),
  nplCount: z.number(),
  auth: z.string(),
  familySize: z.number(),
  kind: z.string(),
  ipcClassSymbol: z.array(z.string()),
  family: z.array(FamilySchema),
});

export type PatentType = z.infer<typeof PatentTypeSchema>;

const InternalDocumentTypeSchema = DocumentBaseSchema.extend({
  type: z.literal(DocumentEnum.INTERNAL_DOCUMENT),
  uploadSource: z.nativeEnum(FileUploadSourceEnum),
  quality: z.nativeEnum(FileQualityEnum),
});

export type InternalDocumentType = z.infer<typeof InternalDocumentTypeSchema>;

export const DocumentSchema = z.discriminatedUnion("type", [
  PatentTypeSchema,
  ArticleTypeSchema,
  InternalDocumentTypeSchema,
]);

export type Document = z.infer<typeof DocumentSchema>;

const DocumentExtraDataExportSchema = z.object({
  clusterId: z.string(),
  url: z.string(),
  entitiesString: z.string(),
});

export type DocumentExtraDataExport = z.infer<
  typeof DocumentExtraDataExportSchema
>;

const DocumentExportSchema = z.intersection(
  DocumentSchema,
  DocumentExtraDataExportSchema,
);

export type DocumentExport = z.infer<typeof DocumentExportSchema>;

export const RequestWithResultSchema = z.object({
  request: RequestDtoSchema,
  result: z.array(DocumentSchema),
});

export type RequestWithResult = z.infer<typeof RequestWithResultSchema>;

export const DeleteFiltersRequestDtoSchema = z.object({
  updateResults: z.boolean(),
});

export type DeleteFiltersRequestDto = z.infer<
  typeof DeleteFiltersRequestDtoSchema
>;
